import { supabase } from '@/integrations/supabase/client';
import { FunctionsHttpError } from '@supabase/supabase-js';
import type { 
  TeamMember, 
  DetailedTeamMember,
  InviteUserInput, 
  ArchiveMemberInput, 
  ReactivateMemberInput, 
  ResendInvitationInput,
  DeleteMemberInput,
  CancelPendingInvitationInput
} from './types';

/**
 * Extract error message from edge function FunctionsHttpError
 * The actual error details are in the Response body (error.context)
 */
const extractEdgeFunctionError = async (
  error: Error,
  fallbackMessage: string
): Promise<string> => {
  try {
    // Check if it's a FunctionsHttpError with a context (Response object)
    if (error instanceof FunctionsHttpError && error.context) {
      const responseBody = await error.context.json();
      if (responseBody && typeof responseBody === 'object' && 'error' in responseBody) {
        return responseBody.error as string;
      }
    }
  } catch {
    // JSON parsing failed, use fallback
  }
  
  // Fallback to error message or default
  return error.message || fallbackMessage;
};

export const teamService = {
  /**
   * Get all team members for the current company
   */
  getTeamMembers: async (): Promise<TeamMember[]> => {
    const { data, error } = await supabase.rpc('list_company_profiles');
    
    if (error) {
      console.error('[teamService] Error fetching team members:', error);
      throw new Error(`Failed to fetch team members: ${error.message}`);
    }

    // Add missing fields for compatibility (RPC already returns created_at)
    return (data || []).map(member => ({
      ...member,
      avatar_url: null,
    }));
  },

  /**
   * Invite a new user to the company
   */
  inviteUser: async (input: InviteUserInput): Promise<void> => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase.functions.invoke('invite-user', {
      body: {
        email: input.email,
        role: input.role, // Legacy support
        roleId: input.roleId, // New role_id support
      },
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
    });

    if (error) {
      console.error('[teamService] Error inviting user:', error);
      const errorMessage = await extractEdgeFunctionError(error, 'Failed to send invitation');
      throw new Error(errorMessage);
    }

    return data;
  },


  /**
   * Archive a team member
   */
  archiveMember: async (input: ArchiveMemberInput): Promise<void> => {
    const { error } = await supabase.rpc('archive_team_member', {
      member_id: input.memberId
    });

    if (error) {
      console.error('[teamService] Error archiving member:', error);
      throw new Error(`Failed to archive team member: ${error.message}`);
    }
  },

  /**
   * Reactivate a team member
   */
  reactivateMember: async (input: ReactivateMemberInput): Promise<void> => {
    const { error } = await supabase.rpc('reactivate_team_member', {
      member_id: input.memberId
    });

    if (error) {
      console.error('[teamService] Error reactivating member:', error);
      throw new Error(`Failed to reactivate team member: ${error.message}`);
    }
  },

  /**
   * Resend invitation to a pending user
   */
  resendInvitation: async (input: ResendInvitationInput): Promise<void> => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase.functions.invoke('resend-invitation', {
      body: {
        userId: input.userId,
      },
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
    });

    if (error) {
      console.error('[teamService] Error resending invitation:', error);
      const errorMessage = await extractEdgeFunctionError(error, 'Failed to resend invitation');
      throw new Error(errorMessage);
    }

    return data;
  },

  /**
   * Get detailed profile information for a specific team member
   */
  getTeamMemberProfile: async (memberId: string): Promise<DetailedTeamMember> => {
    const { data, error } = await supabase.rpc('get_team_member_profile' as any, {
      target_member_id: memberId
    });

    if (error) {
      console.error('[teamService] Error fetching team member profile:', error);
      throw new Error(`Failed to fetch team member profile: ${error.message}`);
    }

    if (!data || !Array.isArray(data) || data.length === 0) {
      throw new Error('Team member not found');
    }

    return data[0] as DetailedTeamMember;
  },

  /**
   * Permanently delete an archived team member
   */
  deleteMember: async (input: DeleteMemberInput): Promise<void> => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase.functions.invoke('delete-team-member', {
      body: {
        memberId: input.memberId,
      },
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
    });

    if (error) {
      console.error('[teamService] Error deleting member:', error);
      const errorMessage = await extractEdgeFunctionError(error, 'Failed to delete team member');
      throw new Error(errorMessage);
    }

    return data;
  },

  /**
   * Cancel a pending invitation (delete uninvited user)
   */
  cancelPendingInvitation: async (input: CancelPendingInvitationInput): Promise<void> => {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      throw new Error('No active session');
    }

    const { data, error } = await supabase.functions.invoke('cancel-pending-invitation', {
      body: {
        memberId: input.memberId,
      },
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
    });

    if (error) {
      console.error('[teamService] Error canceling invitation:', error);
      const errorMessage = await extractEdgeFunctionError(error, 'Failed to cancel invitation');
      throw new Error(errorMessage);
    }

    return data;
  },
};
